/**
 * Keyboard-wedge HID capture as a PURE state machine.
 *
 * The machine consumes plain data events `{ key, timeStampMs }` — timing is
 * data, not wall-clock — so tests need no DOM and no fake timers, and the
 * machine itself never touches `Date`, `performance`, or `setTimeout`.
 * The DOM adapter (`dom-adapter.ts`) is the only place KeyboardEvents exist.
 */

/** A key event as data. `key` uses DOM `KeyboardEvent.key` naming. */
export interface WedgeKeyEvent {
  key: string;
  timeStampMs: number;
}

/** What the machine emits when a buffered burst completes as a scan. */
export interface WedgeEmission {
  /** Buffered characters as received (prefix/suffix included). */
  raw: string;
  /** `raw` with the configured prefix/suffix stripped. */
  value: string;
  /** `timeStampMs` of the terminator key event that completed the scan. */
  timestampMs: number;
}

export interface WedgeMachineOptions {
  /**
   * Keys that terminate a scan (DOM `key` names). Default `["Enter"]`;
   * configure `["Enter", "Tab"]` for scanners programmed with a Tab suffix.
   */
  terminatorKeys?: readonly string[];
  /**
   * Maximum inter-key gap, in ms, for keystrokes to count as one scan burst.
   * Boundary definition: a gap `> interKeyThresholdMs` breaks the burst; a
   * gap EXACTLY EQUAL to the threshold still belongs to the burst
   * (`gap <= threshold` ⇒ burst). The gap between the last character and the
   * terminator key is held to the same rule. Default 35.
   */
  interKeyThresholdMs?: number;
  /** Scanner-programmed prefix to strip when present at the start. Default "". */
  prefix?: string;
  /** Scanner-programmed suffix to strip when present at the end (before the terminator, which is never buffered). Default "". */
  suffix?: string;
  /** Minimum length of the CLEANED value for a burst to emit as a scan. Default 3. */
  minLength?: number;
}

interface ResolvedOptions {
  terminatorKeys: ReadonlySet<string>;
  interKeyThresholdMs: number;
  prefix: string;
  suffix: string;
  minLength: number;
}

export const DEFAULT_TERMINATOR_KEYS: readonly string[] = ["Enter"];
export const DEFAULT_INTER_KEY_THRESHOLD_MS = 35;
export const DEFAULT_MIN_LENGTH = 3;

/**
 * Pure keyboard-wedge state machine. Deterministic: output depends only on
 * the sequence of events fed in. Feed events via {@link feed}; a completed
 * scan is returned from the `feed` call that consumed its terminator.
 *
 * Key classification:
 * - a configured terminator key completes (or discards) the buffer;
 * - any other key whose `key` string is a single character is a data
 *   character (this matches DOM semantics: printable keys — including space —
 *   have single-character `key` values);
 * - everything else (`Shift`, `Control`, `Alt`, `Meta`, `CapsLock`, `F1`…,
 *   `ArrowLeft`…, `Backspace`, `Escape`, `Dead`, `Unidentified`, …) is
 *   IGNORED entirely: it neither enters the buffer nor updates timing, so a
 *   `Shift` interleaved in a burst (scanners press it for uppercase/symbols)
 *   cannot corrupt the buffer. Inter-key gaps are therefore measured between
 *   accepted characters only.
 *
 * Edge-case behavior (each covered by tests):
 * - Terminator as the very first key: no buffer → nothing emitted, machine
 *   stays idle.
 * - Slow gap (`> threshold`) before a character: the pending buffer is human
 *   typing — it is DISCARDED (never emitted) and a fresh buffer starts with
 *   the current character. This is also how a scan arriving while a
 *   human-typed buffer is pending sheds the human prefix.
 * - Slow gap before the terminator: the buffer is human typing followed by a
 *   human Enter/Tab — discarded, nothing emitted (a real wedge sends its
 *   terminator inside the burst).
 * - Two back-to-back scans: the buffer resets on each terminator, and a new
 *   burst may begin at any time after a completed scan — the gap BETWEEN
 *   scans is never compared against the threshold.
 * - A burst shorter than `minLength` (after prefix/suffix stripping) is
 *   discarded, not emitted.
 */
export class KeyboardWedgeMachine {
  private readonly opts: ResolvedOptions;
  private buffer: string[] = [];
  private lastCharAtMs: number | null = null;

  constructor(options: WedgeMachineOptions = {}) {
    this.opts = {
      terminatorKeys: new Set(options.terminatorKeys ?? DEFAULT_TERMINATOR_KEYS),
      interKeyThresholdMs: options.interKeyThresholdMs ?? DEFAULT_INTER_KEY_THRESHOLD_MS,
      prefix: options.prefix ?? "",
      suffix: options.suffix ?? "",
      minLength: options.minLength ?? DEFAULT_MIN_LENGTH,
    };
  }

  /**
   * Feed one key event. Returns a {@link WedgeEmission} when this event
   * completed a scan, otherwise `null`.
   */
  feed(event: WedgeKeyEvent): WedgeEmission | null {
    const { key, timeStampMs } = event;

    if (this.opts.terminatorKeys.has(key)) {
      const emission = this.completeBuffer(timeStampMs);
      this.reset();
      return emission;
    }

    if (key.length !== 1) {
      // Modifier / navigation / function key: invisible to buffer and timing.
      return null;
    }

    if (
      this.lastCharAtMs !== null &&
      timeStampMs - this.lastCharAtMs > this.opts.interKeyThresholdMs
    ) {
      // Human-speed gap: whatever is buffered was typing, not a scan.
      // Discard it and start fresh with this character.
      this.buffer = [];
    }

    this.buffer.push(key);
    this.lastCharAtMs = timeStampMs;
    return null;
  }

  /** Discard any pending buffer (used by adapters on stop()). */
  reset(): void {
    this.buffer = [];
    this.lastCharAtMs = null;
  }

  private completeBuffer(terminatorAtMs: number): WedgeEmission | null {
    if (this.buffer.length === 0 || this.lastCharAtMs === null) {
      return null; // terminator as first key (or after a reset)
    }
    if (terminatorAtMs - this.lastCharAtMs > this.opts.interKeyThresholdMs) {
      return null; // human pressed Enter/Tab after a pause — not a scan
    }

    const raw = this.buffer.join("");
    let value = raw;
    if (this.opts.prefix !== "" && value.startsWith(this.opts.prefix)) {
      value = value.slice(this.opts.prefix.length);
    }
    if (this.opts.suffix !== "" && value.endsWith(this.opts.suffix)) {
      value = value.slice(0, value.length - this.opts.suffix.length);
    }
    if (value.length < this.opts.minLength) {
      return null;
    }
    return { raw, value, timestampMs: terminatorAtMs };
  }
}
